import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { updateUrlWithException } from "../../../../components/Utils";
import { debounce } from "lodash";
import DashboardHeader from "../../../../components/dashboard/dashboardHeader";
import { PlusIcon } from "@heroicons/react/24/outline";
import { ProductsFilter } from "../../../../components/dashboard/products/products-filter";
import { ReportActionEditBar } from "../../../../components/dashboard/service/reports/report-action-edit-bar";
import { PagesTable } from "../../../../components/dashboard/webpage/pagesTable";
import { fetchWebPages } from "../../../../api/webpage/pages";
import { CreateWebpageDialog } from "../../../../components/dashboard/webpage/createWebpageDialog";

export const WebpagesList = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [controller, setController] = useState({
    filters: [],
    page: searchParams.get("page") ? searchParams.get("page") : 1,
    per_page: searchParams.get("per_page") ? searchParams.get("per_page") : 50,
    query: searchParams.get("query") ? searchParams.get("query") : "",
    sort: searchParams.get("sort") ? searchParams.get("sort") : "ASC",
    sortBy: searchParams.get("sortBy") ? searchParams.get("sortBy") : "id",
    view: searchParams.get("view") ? searchParams.get("view") : "all",
    fields: searchParams.get("fields")
      ? searchParams.get("fields")
      : "id,name,email,mobile,roles",
  });

  const [pagesState, setPagesState] = useState({
    data: [],
    isLoading: false,
    errors: null,
    count: 0,
    currentCount: 0,
  });

  // Update all params from controller except filters (dont't touch filters)
  useEffect(() => {
    updateUrlWithException(
      // These will be updated
      {
        page: controller.page,
        per_page: controller.per_page,
        query: controller.query,
        sort: controller.sort,
        sortBy: controller.sortBy,
        view: controller.view,
      },
      // This will be preserved
      "filters"
    );
  }, [controller]);

  const { isLoading } = pagesState;
  const [selectedItems, setSelectedItems] = useState([]);

  const handleQueryChange = (newQuery) => {
    setController({
      ...controller,
      page: 1,
      query: newQuery,
    });
  };

  const handlePageChange = (newPage) => {
    setController({
      ...controller,
      page: newPage,
    });
  };

  const handlePagePageChange = (option) => {
    setController({
      ...controller,
      per_page: option,
    });
  };

  const handleFilterChange = (selectedFilters) => {
    setController({
      ...controller,
      filters: selectedFilters?.map((filter) => {
        return {
          name: filter.id,
          value: JSON.stringify(filter.values),
        };
      }),
    });
  };

  const getPages = async (controller) => {
    setPagesState({ ...pagesState, isLoading: true });
    const response = await fetchWebPages(controller);
    if (response?.data) {
      setPagesState({
        ...pagesState,
        data: response.data?.data,
        count: response.data?.count,
        currentCount: response.data?.currentCount,
        isLoading: false,
      });
    } else {
      setPagesState({
        ...pagesState,
        data: [],
        count: 0,
        currentCount: 0,
        isLoading: false,
        errors: response.errors,
      });
    }
  };

  const debouncedDispatchAction = debounce((controller) => {
    getPages(controller);
  }, 1000);

  useEffect(() => {
    // Trigger the debounced dispatch action
    debouncedDispatchAction(controller);

    // Clean the debounced function on component unmount.
    return () => {
      debouncedDispatchAction.cancel();
    };
  }, [controller]);

  const sortByOptions = [
    { name: "Seneste (Rapportnr.)", sort: "asc", sortBy: "report_number" },
    { name: "Eldste (Rapportnr.)", sort: "desc", sortBy: "report_number" },
  ];

  const initialFilters = [];

  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  return (
    <>
      <DashboardHeader
        subtitle=""
        actions={[]}
        title={"Pages"}
        meta={[]}
        mainButton={{
          label: "New",
          action: () => setCreateDialogOpen(true),
          path: null,
          color: "",
          icon: PlusIcon,
        }}
      >
        <div className="mx-auto mt-8 grid max-w-3xl grid-cols-1 gap-6 sm:px-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-3 lg:col-start-1 sm:col-span-3">
            <ProductsFilter
              className={"mt-10"}
              disabled={isLoading}
              onQueryChange={handleQueryChange}
              query={controller.query}
              selectedItems={selectedItems}
              sortByOptions={sortByOptions}
              initialFilters={initialFilters}
              onFilterChange={handleFilterChange}
              defaultSelectedOptions={[]}
            />

            <PagesTable
              onItemsSelect={(items) => setSelectedItems(items)}
              itemState={pagesState}
              itemsCount={pagesState.count}
              currentCount={pagesState.currentCount}
              page={controller.page}
              onPageChange={handlePageChange}
              perPage={controller.per_page}
            />
          </div>
        </div>
      </DashboardHeader>

      <CreateWebpageDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
      />
    </>
  );
};
